import { useState, useCallback, useMemo } from 'react';
import { Box, Typography, Button, Grid, Alert, Chip, Snackbar, Paper } from '@mui/material';
import { Add, Refresh } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useWorkflowList, WorkflowCard } from '@/components/workflow';
import { useDataProvider } from 'react-admin';

const CLIENT_ACTIONS = [
  { event: 'ACTIVATE', label: 'Activate', color: 'primary' as const },
  { event: 'DEACTIVATE', label: 'Deactivate', color: 'error' as const },
];

export const ClientWorkflowManager = () => {
  const navigate = useNavigate();
  const dataProvider = useDataProvider();

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const { workflows, total, isLoading, refetch } = useWorkflowList({
    dataProvider,
    filters: { workflowType: ['client'] },
    perPage: 100,
  });

  const stats = useMemo(() => {
    const active = workflows.filter(w => w.status === 'active').length;
    const completed = workflows.filter(w => w.status === 'completed').length;
    const cancelled = workflows.filter(w => w.status === 'cancelled').length;
    return { active, completed, cancelled };
  }, [workflows]);

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleAdvance = useCallback(async (workflowId: string, event: string) => {
    try {
      const response = await fetch(
        `/api/v1/workflow-instances/${workflowId}/transition`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ event }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Transition failed');
      }

      setSnackbar({ open: true, message: 'Workflow advanced successfully', severity: 'success' });
      refetch();
    } catch {
      setSnackbar({ open: true, message: 'Failed to advance workflow', severity: 'error' });
    }
  }, [refetch]);

  const handlePause = useCallback(async (workflowId: string) => {
    try {
      const workflow = workflows.find(w => w.id === workflowId);
      if (!workflow || !dataProvider) return;

      await dataProvider.update('workflow-instances', {
        id: workflowId,
        data: { ...workflow, status: 'paused', pausedAt: new Date().toISOString() },
        previousData: workflow,
      });
      setSnackbar({ open: true, message: 'Workflow paused', severity: 'success' });
      refetch();
    } catch {
      setSnackbar({ open: true, message: 'Failed to pause workflow', severity: 'error' });
    }
  }, [workflows, dataProvider, refetch]);

  const handleResume = useCallback(async (workflowId: string) => {
    try {
      const workflow = workflows.find(w => w.id === workflowId);
      if (!workflow || !dataProvider) return;

      await dataProvider.update('workflow-instances', {
        id: workflowId,
        data: { ...workflow, status: 'active', pausedAt: null },
        previousData: workflow,
      });
      setSnackbar({ open: true, message: 'Workflow resumed', severity: 'success' });
      refetch();
    } catch {
      setSnackbar({ open: true, message: 'Failed to resume workflow', severity: 'error' });
    }
  }, [workflows, dataProvider, refetch]);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Client Workflows
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage client lifecycle workflows
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={handleRefresh} disabled={isLoading}>
            Refresh
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/clients/create')}>
            New Client
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip label={`${stats.active} Active`} color="primary" size="small" sx={{ fontWeight: 600 }} />
          <Typography variant="body2" color="text.secondary">|</Typography>
          <Chip label={`${stats.completed} Completed`} color="success" size="small" sx={{ fontWeight: 600 }} />
          <Typography variant="body2" color="text.secondary">|</Typography>
          <Chip label={`${stats.cancelled} Cancelled`} color="error" size="small" sx={{ fontWeight: 600 }} />
        </Box>
      </Paper>

      {isLoading ? (
        <Typography sx={{ py: 4, textAlign: 'center' }}>Loading workflows...</Typography>
      ) : workflows.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
            No client workflows found
          </Typography>
          <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/clients/create')}>
            New Client
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {workflows.map((workflow) => (
            <Grid key={workflow.id} size={{ xs: 12, md: 6, lg: 4 }}>
              <WorkflowCard
                workflow={workflow}
                onPause={handlePause}
                onResume={handleResume}
                onAdvance={handleAdvance}
              />
            </Grid>
          ))}
        </Grid>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
